import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import { useAuth } from '../../../contexts/AuthContext';
import AccountGroupModal from './AccountGroupModal';
import { ACCOUNTING_API, ECOMMERCE_API_URL, handleApiError } from '../../../config/api';
import { accountingApi, ecommerceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

// Cari grup arayüzü
interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
  /** SQL'deki sayısal grup ID'si (string olarak) — id alanı Firebase ID taşıyabildiği için ayrı tutulur. */
  sqlId?: string | null;
}

// Cari hesap arayüzü
interface AccountData {
  companyName: string;
  groupId: string | null;
  authorizedPerson: string;
  authorizedPersonPhone: string;
  phone: string;
  mobilePhone: string;
  email: string;
  website: string;
  postalCode: string;
  taxOffice: string;
  taxNumber: string;
  tradeRegisterNumber: string;
  country: string;
  city: string;
  district: string;
  address: string;
  accountCode: string;
  notes: string;
  branchId: string | null;
  riskLimitAmount: string;
  riskLimitCurrency: string;
  paymentDueDays: string;
  riskLimitAction: string;
  preventNegativeBalance: boolean;
  // ===== E-Ticaret / B2B (Faz 5.2) =====
  isEcommerceUser: boolean;
  isB2BDealer: boolean;
  ecomUsername: string;
  ecomPassword: string;
  ecommerceDiscountPercent: string;
  ecommercePriceBasis: string;
  blockOrdersWhenOverdue: boolean;
}

// Şube arayüzü
interface Branch {
  id: string;
  name: string;
}

interface AddAccountProps {
  open: boolean;
  onClose: () => void;
  editAccount?: any; // ✅ Düzenlenecek cari (varsa düzenleme modu)
  prefillData?: any; // ✅ YENİ: E-faturadan gelen bilgiler ile pre-fill için
  /** Manager panelinden açıldığında şube alanı bu şubeye kilitlenir */
  lockedBranchId?: string | null;
}

const AddAccount: React.FC<AddAccountProps> = ({ open, onClose, editAccount, prefillData, lockedBranchId = null }) => {
  const { t } = useTranslation('accounting');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches } = useAuth();
  const defaultCountry = t('current.modals.addAccount.defaults.country');
  const localizeGroupName = (name: string) => {
    const normalized = name.trim().toLocaleLowerCase('tr-TR');
    const keyMap: Record<string, string> = {
      'bireysel cari': 'individualCurrent',
      'personel cari': 'personnelCurrent',
      'personal cari': 'personnelCurrent',
      'kurumsal cari': 'corporateCurrent',
      'genel cari grubu': 'generalCurrentGroup'
    };
    const key = keyMap[normalized];
    return key ? t(`current.groupNames.${key}`, { defaultValue: name }) : name;
  };
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  
  // ✅ Faz 5.2: Cariye bağlı mevcut e-ticaret üyesi (düzenleme modunda yüklenir)
  const [linkedMember, setLinkedMember] = useState<any | null>(null);
  // Cari kaydedildi ama e-ticaret üyesi adımı hata verdiyse, tekrar Kaydet'e
  // basıldığında yeni bir cari AÇILMAMASI için oluşturulan cari id'si saklanır.
  const createdAccountIdRef = useRef<number | null>(null);
  
  // ✅ YENİ: Düzenleme modu kontrolü
  const isEditMode = !!editAccount;
  
  // Cari hesap verileri
  const [accountData, setAccountData] = useState<AccountData>({
    companyName: '',
    groupId: null,
    authorizedPerson: '',
    authorizedPersonPhone: '',
    phone: '',
    mobilePhone: '',
    email: '',
    website: '',
    postalCode: '',
    taxOffice: '',
    taxNumber: '',
    tradeRegisterNumber: '',
    country: defaultCountry,
    city: '',
    district: '',
    address: '',
    accountCode: '',
    notes: '',
    branchId: null,
    riskLimitAmount: '0',
    riskLimitCurrency: 'TRY',
    paymentDueDays: '30',
    riskLimitAction: 'işlem yaptır',
    preventNegativeBalance: false,
    isEcommerceUser: false,
    isB2BDealer: false,
    ecomUsername: '',
    ecomPassword: '',
    ecommerceDiscountPercent: '',
    ecommercePriceBasis: 'SalePrice',
    blockOrdersWhenOverdue: false
  });
  
  // Cari gruplar
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  
  // Para birimi seçenekleri için state
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);
  
  // Şirket ID'si ve grupları yükleme
  useEffect(() => {
    if (open) {
      loadCompanyAndGroups();
    }
  }, [open]);
  
  // ✅ YENİ: Düzenleme modunda mevcut veriyi yükle
  useEffect(() => {
    if (open && editAccount && groups.length > 0) {
      console.log('🔄 Düzenleme modu: Mevcut cari verisi yükleniyor...', editAccount);
      
      // Form verilerini doldur
      setAccountData({
        companyName: editAccount.companyName || '',
        groupId: editAccount.groupId || null,
        authorizedPerson: editAccount.authorizedPerson || '',
        authorizedPersonPhone: editAccount.authorizedPersonPhone || '',
        phone: editAccount.phone || '',
        mobilePhone: editAccount.mobilePhone || '',
        email: editAccount.email || '',
        website: editAccount.website || '',
        postalCode: editAccount.postalCode || '',
        taxOffice: editAccount.taxOffice || '',
        taxNumber: editAccount.taxNumber || '',
        tradeRegisterNumber: editAccount.tradeRegisterNumber || '',
        country: editAccount.country || defaultCountry,
        city: editAccount.city || '',
        district: editAccount.district || '',
        address: editAccount.address || '',
        accountCode: editAccount.accountCode || '',
        notes: editAccount.notes || '',
        branchId: editAccount.branchId || null,
        riskLimitAmount: editAccount.riskLimitAmount?.toString() || '0',
        riskLimitCurrency: editAccount.riskLimitCurrency || 'TRY',
        paymentDueDays: editAccount.paymentDueDays?.toString() || '30',
        riskLimitAction: editAccount.riskLimitAction || 'işlem yaptır',
        preventNegativeBalance: editAccount.preventNegativeBalance || false,
        isEcommerceUser: editAccount.isEcommerceUser || false,
        isB2BDealer: editAccount.isB2BDealer || false,
        ecomUsername: '',
        ecomPassword: '',
        ecommerceDiscountPercent: editAccount.ecommerceDiscountPercent?.toString() || '',
        ecommercePriceBasis: editAccount.ecommercePriceBasis || 'SalePrice',
        blockOrdersWhenOverdue: editAccount.blockOrdersWhenOverdue || false
      });
      
      // Grup seçimini ayarla — carideki referans Firebase ID veya SQL ID olabilir
      const selectedGrp = groups.find(
        g => g.id === editAccount.groupId || (g.sqlId && g.sqlId === editAccount.groupId)
      );
      setSelectedGroup(selectedGrp || null);
      
      // Şube seçimini ayarla
      const selectedBr = branches.find(b => b.id === editAccount.branchId);
      setSelectedBranch(selectedBr || null);
      
      console.log('✅ Düzenleme modu: Veriler yüklendi');

      // ✅ Faz 5.2: Cariye bağlı e-ticaret üyesi varsa kullanıcı adını göster
      const numericId = parseInt(editAccount.id, 10);
      if (!isNaN(numericId)) {
        ecommerceApi
          .get(`${ECOMMERCE_API_URL}/api/ecommerce/members/auth/by-account/${numericId}`)
          .then(res => {
            const m = res.data?.data;
            if (m) {
              setLinkedMember(m);
              setAccountData(prev => ({
                ...prev,
                isEcommerceUser: true,
                isB2BDealer: prev.isB2BDealer || !!m.isB2B,
                ecomUsername: m.username || ''
              }));
            } else {
              setLinkedMember(null);
            }
          })
          .catch(err => {
            console.warn('[AddAccount] Bağlı e-ticaret üyesi sorgulanamadı:', err);
          });
      }
    }
  }, [open, editAccount, groups, branches, defaultCountry]);
  
  // ✅ YENİ: E-faturadan gelen bilgiler ile pre-fill
  useEffect(() => {
    if (open && prefillData && !editAccount) {
      console.log('📧 [E-INVOICE] Cari formu pre-fill ediliyor...', prefillData);
      
      setAccountData({
        companyName: prefillData.companyName || '',
        groupId: null, // Kullanıcı seçecek
        authorizedPerson: prefillData.authorizedPerson || t('current.modals.addAccount.defaults.authorizedPerson'),
        authorizedPersonPhone: '',
        phone: prefillData.phone || '',
        mobilePhone: '',
        email: prefillData.email || '',
        website: prefillData.website || '',
        postalCode: '',
        taxOffice: prefillData.taxOffice || '',
        taxNumber: prefillData.taxNumber || '',
        tradeRegisterNumber: '',
        country: prefillData.country || defaultCountry,
        city: prefillData.city || '',
        district: prefillData.district || '',
        address: prefillData.address || '',
        accountCode: '',
        notes: t('current.modals.addAccount.defaults.prefillNote'),
        branchId: null,
        riskLimitAmount: '0',
        riskLimitCurrency: 'TRY',
        paymentDueDays: '30',
        riskLimitAction: 'işlem yaptır',
        preventNegativeBalance: false,
        isEcommerceUser: false,
        isB2BDealer: false,
        ecomUsername: '',
        ecomPassword: '',
        ecommerceDiscountPercent: '',
        ecommercePriceBasis: 'SalePrice',
        blockOrdersWhenOverdue: false
      });
      
      console.log('✅ [E-INVOICE] Form pre-fill edildi');
    }
  }, [open, prefillData, editAccount, t, defaultCountry]);

  // ✅ Manager: şube alanı kilitli — manager'ın şubesi otomatik seçilir
  useEffect(() => {
    if (open && lockedBranchId) {
      const lockedBr = branches.find(b => b.id === lockedBranchId);
      if (lockedBr) {
        setSelectedBranch(lockedBr);
        setAccountData(prev => ({ ...prev, branchId: lockedBr.id }));
      }
    }
  }, [open, lockedBranchId, branches]);
  
  const loadCompanyAndGroups = async () => {
    try {
      setInitialLoading(true);
      
      // Kullanıcı oturum açmış mı kontrol et
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.userNotLoggedIn'));
        return;
      }
      
      // Kullanıcının şirket ID'sini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.userNotFound'));
        return;
      }
      
      const companyId = userData.companyId;
      setCompanyId(companyId || null);
      
      if (!companyId) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.companyNotFound'));
        return;
      }
      
      // Cari grupları yükle
      loadAccountGroups(companyId);
      
      // Para birimlerini yükle
      loadCurrencies(companyId);
      
    } catch (error) {
      console.error('Şirket veya grup verileri yüklenirken hata:', error);
      setInitialLoading(false);
      setError(t('current.modals.addAccount.errors.dataLoad'));
    }
  };
  
  // Cari gruplarını yükleme
  const loadAccountGroups = async (companyId: string) => {
    try {
      console.log('SQL\'den cari gruplar yükleniyor...');
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY);

      const result = response.data;
      const sqlGroups = result.data || result;
      
      // SQL'den gelen verileri frontend formatına çevir
      const groupsList: AccountGroup[] = sqlGroups.map((data: any) => ({
        id: data.firebaseId || data.id.toString(), // Firebase ID varsa onu kullan
        sqlId: data.id != null ? data.id.toString() : null,
        name: data.name || '',
        parentId: data.parentFirebaseId || null,
        level: data.level || 1,
        createdAt: data.createdAt || '',
        branchesId: data.branchId || null
      }));
      
      // Grupları seviyeye göre sırala
      groupsList.sort((a, b) => {
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        return a.name.localeCompare(b.name);
      });
      
      setGroups(groupsList);
      setInitialLoading(false);
      console.log(`✅ ${groupsList.length} cari grup SQL'den yüklendi`);
    } catch (error) {
      console.error('SQL cari grupları yükleme hatası:', error);
      setError(handleApiError(error));
      setInitialLoading(false);
    }
  };
  
  // Para birimlerini yükle (TEK SEFERLİK - Cloud Functions)
  const loadCurrencies = async (companyId: string) => {
    try {
      const currencyData = await getCurrencySettings(companyId);
      
      // Varsayılan para birimi TRY'yi hazırla
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true } // TRY her zaman aktif olacak
      ];
      
      if (currencyData) {
        
        // Veritabanından gelen para birimleri
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          // TRY zaten eklendiği için tekrar eklemeye gerek yok
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      console.log('Aktif para birimleri yüklendi:', currencies);
      setAvailableCurrencies(currencies);
      
      // Eğer seçili para birimi artık mevcut değilse, TRY'ye ayarla
      const riskLimitCurrencyExists = currencies.some(c => c.code === accountData.riskLimitCurrency && c.active);
      
      if (!riskLimitCurrencyExists) {
        setAccountData(prev => ({
          ...prev,
          riskLimitCurrency: 'TRY'
        }));
      }
      
    } catch (error) {
      console.error('Para birimi yükleme hatası:', error);
      // Offline fallback
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Para birimi sembolünü döndür
  const getCurrencySymbol = (code: string): React.ReactNode => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };
  
  // Form verilerini temizle
  const resetForm = () => {
    setAccountData({
      companyName: '',
      groupId: null,
      authorizedPerson: '',
      authorizedPersonPhone: '',
      phone: '',
      mobilePhone: '',
      email: '',
      website: '',
      postalCode: '',
      taxOffice: '',
      taxNumber: '',
      tradeRegisterNumber: '',
      country: defaultCountry,
      city: '',
      district: '',
      address: '',
      accountCode: '',
      notes: '',
      branchId: null,
      riskLimitAmount: '0',
      riskLimitCurrency: 'TRY',
      paymentDueDays: '30',
      riskLimitAction: 'işlem yaptır',
      preventNegativeBalance: false,
      isEcommerceUser: false,
      isB2BDealer: false,
      ecomUsername: '',
      ecomPassword: '',
      ecommerceDiscountPercent: '',
      ecommercePriceBasis: 'SalePrice',
      blockOrdersWhenOverdue: false
    });
    setSelectedGroup(null);
    setSelectedBranch(null);
    setLinkedMember(null);
    createdAccountIdRef.current = null;
    setError(null);
  };
  
  // Modal kapatılırken
  const handleClose = () => {
    resetForm();
    setInfoDialogOpen(false);
    onClose();
  };
  
  // Grup seçimi değiştiğinde
  const handleGroupChange = (value: AccountGroup | null) => {
    setSelectedGroup(value);
    setAccountData({
      ...accountData,
      groupId: value ? value.id : null
    });
  };
  
  // Şube seçimi değiştiğinde
  const handleBranchChange = (value: Branch | null) => {
    setSelectedBranch(value);
    setAccountData({
      ...accountData,
      branchId: value ? value.id : null
    });
  };
  
  // Input değişimini işle
  const handleChange = (field: keyof AccountData, value: string | boolean) => {
    setAccountData({
      ...accountData,
      [field]: value
    });
  };
  
  // Formu gönder (Yeni ekleme veya güncelleme)
  const handleSubmit = async () => {
    // Validasyon
    if (!accountData.companyName.trim()) {
      setError(t('current.modals.addAccount.validation.companyNameRequired'));
      return;
    }
    
    if (!accountData.groupId) {
      setError(t('current.modals.addAccount.validation.groupRequired'));
      return;
    }
    
    if (!accountData.authorizedPerson.trim()) {
      setError(t('current.modals.addAccount.validation.authorizedPersonRequired'));
      return;
    }
    
    if (!accountData.taxNumber.trim()) {
      setError(t('current.modals.addAccount.validation.taxNumberRequired'));
      return;
    }
    
    // ✅ Faz 5.2: E-ticaret kullanıcısı doğrulamaları
    if (accountData.isEcommerceUser) {
      if (!accountData.ecomUsername.trim()) {
        setError(t('current.modals.addAccount.b2b.validation.usernameRequired', { defaultValue: 'E-ticaret kullanıcısı için kullanıcı adı zorunludur.' }));
        return;
      }
      // E-posta CARİ KAYDINDAN ÖNCE doğrulanır: backend create-from-account e-posta
      // ister; eksikse cari oluşup üye adımı patlıyor ve tekrar Kaydet duplicate
      // cari açıyordu. Ön kontrol bu senaryoyu baştan engeller.
      if (!accountData.email.trim()) {
        setError(t('current.modals.addAccount.b2b.validation.emailRequired', { defaultValue: 'E-ticaret kullanıcısı için e-posta zorunludur.' }));
        return;
      }
      if (!linkedMember && !accountData.ecomPassword) {
        setError(t('current.modals.addAccount.b2b.validation.passwordRequired', { defaultValue: 'Yeni e-ticaret kullanıcısı için şifre zorunludur.' }));
        return;
      }
      if (accountData.ecomPassword && accountData.ecomPassword.length < 6) {
        setError(t('current.modals.addAccount.b2b.validation.passwordMinLength', { defaultValue: 'Şifre en az 6 karakter olmalıdır.' }));
        return;
      }
    }
    
    if (accountData.ecommerceDiscountPercent) {
      const pct = parseFloat(accountData.ecommerceDiscountPercent);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        setError(t('current.modals.addAccount.b2b.validation.discountRange', { defaultValue: 'İskonto yüzdesi 0-100 arasında olmalıdır.' }));
        return;
      }
    }
    
    // Kaydetme veya güncelleme işlemi
    setLoading(true);
    setError(null);
    
    try {
      if (!companyId) {
        throw new Error(t('current.modals.addAccount.errors.companyNotFound'));
      }
      
      // SQL API için veri hazırla
      const accountPayload: any = {
        companyId: companyId,
        companyName: accountData.companyName.trim(),
        groupFirebaseId: selectedGroup?.id ?? accountData.groupId,
        // Sayısal FK'yı da gönder: liste/rapor sorguları GroupId üzerinden filtreliyor,
        // yalnız groupFirebaseId doldurulduğunda grup filtresi sonuç getirmiyordu.
        groupId: selectedGroup?.sqlId ? parseInt(selectedGroup.sqlId, 10) : null,
        authorizedPerson: accountData.authorizedPerson.trim(),
        phone: accountData.phone.trim(),
        mobilePhone: accountData.mobilePhone.trim(),
        email: accountData.email.trim(),
        website: accountData.website.trim(),
        postalCode: accountData.postalCode.trim(),
        taxOffice: accountData.taxOffice.trim(),
        taxNumber: accountData.taxNumber.trim(),
        tradeRegisterNumber: accountData.tradeRegisterNumber.trim(),
        country: accountData.country.trim(),
        city: accountData.city.trim(),
        district: accountData.district.trim(),
        address: accountData.address.trim(),
        accountCode: accountData.accountCode.trim() || '',
        notes: accountData.notes.trim(),
        branchId: selectedBranch?.id || null,
        riskLimitAmount: accountData.riskLimitAmount ? parseFloat(accountData.riskLimitAmount) : null,
        riskLimitCurrency: accountData.riskLimitCurrency || 'TRY',
        paymentDueDays: accountData.paymentDueDays ? parseInt(accountData.paymentDueDays) : null,
        riskLimitAction: accountData.riskLimitAction || null,
        preventNegativeBalance: accountData.preventNegativeBalance || false,
        // ✅ Faz 5.2: E-ticaret/B2B alanları
        ecommerceDiscountPercent: accountData.ecommerceDiscountPercent ? parseFloat(accountData.ecommerceDiscountPercent) : null,
        ecommercePriceBasis: accountData.ecommercePriceBasis || 'SalePrice',
        blockOrdersWhenOverdue: accountData.blockOrdersWhenOverdue || false,
        isActive: editAccount?.isActive !== undefined ? editAccount.isActive : true,
      };
      
      let apiUrl;
      
      if (isEditMode) {
        // ✅ GÜNCELLEME MODU
        const numericId = parseInt(editAccount.id, 10);
        if (isNaN(numericId)) {
          throw new Error(t('current.modals.addAccount.errors.invalidAccountId'));
        }
        
        accountPayload.id = numericId;
        accountPayload.updatedAt = new Date().toISOString();
        
        apiUrl = ACCOUNTING_API.ACCOUNT_UPDATE(numericId);
        console.log('🔄 SQL API\'ye cari hesap güncelleniyor:', accountPayload);
      } else if (createdAccountIdRef.current) {
        // Cari önceki denemede zaten oluşturuldu (üye adımı hata vermişti):
        // ikinci Kaydet duplicate cari AÇMAZ, mevcut kaydı günceller.
        accountPayload.id = createdAccountIdRef.current;
        accountPayload.updatedAt = new Date().toISOString();
        apiUrl = ACCOUNTING_API.ACCOUNT_UPDATE(createdAccountIdRef.current);
        console.log('🔄 Önceki denemede oluşan cari güncelleniyor (duplicate önlendi):', accountPayload);
      } else {
        // ✅ YENİ EKLEME MODU
        apiUrl = ACCOUNTING_API.ACCOUNTS;
        console.log('🔄 SQL API\'ye yeni cari hesap ekleniyor:', accountPayload);
      }

      const response = await accountingApi.post(apiUrl, accountPayload);
      const result = response.data;
      console.log(`✅ Cari hesap SQL'de başarıyla ${isEditMode ? 'güncellendi' : 'kaydedildi'}:`, result);

      // Yeni oluşturulan cari id'sini sakla — üye adımı patlarsa bir sonraki
      // Kaydet aynı cariyi güncellesin, yenisini oluşturmasın.
      if (!isEditMode && !createdAccountIdRef.current) {
        const newId = result?.data?.id ?? result?.id;
        if (newId) createdAccountIdRef.current = Number(newId);
      }
      
      // ✅ Faz 5.2: E-ticaret kullanıcısı tiki işaretliyse üye oluştur/güncelle
      if (accountData.isEcommerceUser) {
        const savedAccountId = isEditMode
          ? parseInt(editAccount.id, 10)
          : (createdAccountIdRef.current ?? (result?.data?.id ?? result?.id));
        
        if (savedAccountId) {
          try {
            const memberPayload: any = {
              accountId: savedAccountId,
              username: accountData.ecomUsername.trim(),
              isB2B: accountData.isB2BDealer,
              email: accountData.email.trim() || undefined
            };
            if (accountData.ecomPassword) {
              memberPayload.password = accountData.ecomPassword;
            }
            
            const memberRes = await ecommerceApi.post(
              `${ECOMMERCE_API_URL}/api/ecommerce/members/auth/create-from-account`,
              memberPayload
            );
            console.log('✅ [B2B] E-ticaret üyesi oluşturuldu/güncellendi:', memberRes.data);
          } catch (memberError: any) {
            console.error('❌ [B2B] E-ticaret üyesi oluşturulamadı:', memberError);
            const memberMsg = memberError?.response?.data?.message
              || t('current.modals.addAccount.b2b.errors.memberCreateFailed', { defaultValue: 'Cari kaydedildi ancak e-ticaret kullanıcısı oluşturulamadı.' });
            setError(memberMsg);
            setLoading(false);
            return; // Modalı açık bırak, kullanıcı hatayı görüp düzeltebilsin
          }
        }
      }
      
      // Başarılı olunca modalı kapat
      handleClose();
    } catch (error: any) {
      console.error(`❌ Cari hesap ${isEditMode ? 'güncellenirken' : 'kaydedilirken'} hata:`, error);
      const errorData = error.response?.data;
      if (errorData) {
        if (errorData.errors || errorData.validationDetails) {
          console.error('❌ Validation Hataları:', errorData.errors);
          console.error('❌ Validation Detayları:', errorData.validationDetails);
        }
        let userMessage = errorData.message || (isEditMode ? t('current.modals.addAccount.errors.updateFailed') : t('current.modals.addAccount.errors.saveFailed'));
        if (userMessage.includes('cari kodu zaten kullanılıyor')) {
          userMessage = t('current.modals.addAccount.errors.accountCodeDuplicate');
        }
        setError(userMessage);
      } else {
        setError(error instanceof Error ? error.message : t('current.modals.addAccount.errors.unknown'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Grup adını tam yoluyla göster
  const getGroupFullPath = (group: AccountGroup | null): string => {
    if (!group) return '';
    
    const parts: string[] = [localizeGroupName(group.name)];
    let currentParentId = group.parentId;
    
    while (currentParentId) {
      const parentGroup = groups.find(g => g.id === currentParentId);
      if (parentGroup) {
        parts.unshift(localizeGroupName(parentGroup.name));
        currentParentId = parentGroup.parentId;
      } else {
        break;
      }
    }
    
    return parts.join(' > ');
  };
  
  // Cari Grup Modalını açma
  const handleOpenGroupModal = () => {
    setIsGroupModalOpen(true);
  };

  // Cari Grup Modalını kapatma
  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
    // Modal kapandığında grupları yeniden yükle
    if (companyId) {
      loadAccountGroups(companyId);
    }
  };
  
  // Grup seçimleri için grupları filtrele
  const getFilteredGroupOptions = () => {
    // Önce ana grupları (level 1) alıyoruz
    const rootGroups = groups.filter(group => group.level === 1);
    
    // Sonuç listesini oluşturuyoruz
    const options: {
      group: AccountGroup;
      label: string;
      level: number;
      isParent: boolean;
      parentName?: string;
    }[] = [];
    
    // Ana grupları ve alt grupları düzenliyoruz
    rootGroups.forEach(rootGroup => {
      // Ana grubu ekle
      options.push({
        group: rootGroup,
        label: localizeGroupName(rootGroup.name),
        level: rootGroup.level,
        isParent: true
      });
      
      // Alt grupları bul ve ekle (Level 2)
      const subGroups = groups.filter(group => group.parentId === rootGroup.id);
      subGroups.forEach(subGroup => {
        options.push({
          group: subGroup,
          label: localizeGroupName(subGroup.name),
          level: subGroup.level,
          isParent: false,
          parentName: localizeGroupName(rootGroup.name)
        });
        
        // Alt grupların alt gruplarını bul ve ekle (Level 3)
        const subSubGroups = groups.filter(group => group.parentId === subGroup.id);
        subSubGroups.forEach(subSubGroup => {
          options.push({
            group: subSubGroup,
            label: localizeGroupName(subSubGroup.name),
            level: subSubGroup.level,
            isParent: false,
            parentName: `${localizeGroupName(rootGroup.name)} > ${localizeGroupName(subGroup.name)}`
          });
        });
      });
    });
    
    return options;
  };
  
  // Formun içeriğini render et
  const renderFormContent = () => (
    <Grid container spacing={3}>
      {/* Zorunlu Alan Uyarısı */}
      <Grid item xs={12}>
        <Alert severity="info" icon={false} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <span style={{ color: 'red', fontWeight: 'bold' }}>*</span> {t('current.modals.addAccount.requiredInfo')}
          </Typography>
        </Alert>
      </Grid>

      {/* Cari Bilgiler Bölümü */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('current.modals.addAccount.sections.currentInfo')}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                label={t('current.modals.addAccount.fields.companyName')}
                value={accountData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
                sx={{ 
                  '& .MuiInputLabel-root': { 
                    color: accountData.companyName.trim() === '' ? 'error.main' : 'inherit' 
                  },
                  '& .MuiOutlinedInput-root': { 
                    '& fieldset': { 
                      borderColor: accountData.companyName.trim() === '' ? 'error.main' : 'inherit' 
                    },
                    '&:hover fieldset': { 
                      borderColor: accountData.companyName.trim() === '' ? 'error.main' : 'inherit' 
                    },
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={getFilteredGroupOptions()}
                  getOptionLabel={(option) => {
                    if (option.level === 1) {
                      return localizeGroupName(option.group.name);
                    }
                    return `${option.parentName} > ${option.label}`;
                  }}
                  value={selectedGroup ? {
                    group: selectedGroup,
                    label: localizeGroupName(selectedGroup.name),
                    level: selectedGroup.level,
                    isParent: selectedGroup.level === 1,
                    parentName: getGroupFullPath(selectedGroup).split(' > ').slice(0, -1).join(' > ') || undefined
                  } : null}
                  onChange={(_, value) => handleGroupChange(value ? value.group : null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('current.modals.addAccount.fields.group')}
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <GroupIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                      sx={{ 
                        '& .MuiInputLabel-root': { 
                          color: !accountData.groupId ? 'error.main' : 'inherit' 
                        },
                        '& .MuiOutlinedInput-root': { 
                          '& fieldset': { 
                            borderColor: !accountData.groupId ? 'error.main' : 'inherit' 
                          },
                          '&:hover fieldset': { 
                            borderColor: !accountData.groupId ? 'error.main' : 'inherit' 
                          },
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ 
                        pl: (option.level - 1) * 2, 
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        fontWeight: option.isParent ? 'bold' : 'normal',
                        color: option.isParent ? 'text.primary' : 'text.secondary'
                      }}>
                        <GroupIcon 
                          fontSize="small" 
                          sx={{ 
                            mr: 1, 
                            color: option.isParent ? 'primary.main' : 'action.active',
                            opacity: option.isParent ? 1 : 0.7
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: option.isParent ? 'bold' : 'normal'
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenGroupModal}
                  sx={{ 
                    minWidth: '40px', 
                    height: '40px',
                    padding: 0,
                    borderRadius: '4px'
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                label={t('current.modals.addAccount.fields.authorizedPerson')}
                value={accountData.authorizedPerson}
                onChange={(e) => handleChange('authorizedPerson', e.target.value)}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
                sx={{ 
                  '& .MuiInputLabel-root': { 
                    color: accountData.authorizedPerson.trim() === '' ? 'error.main' : 'inherit' 
                  },
                  '& .MuiOutlinedInput-root': { 
                    '& fieldset': { 
                      borderColor: accountData.authorizedPerson.trim() === '' ? 'error.main' : 'inherit' 
                    },
                    '&:hover fieldset': { 
                      borderColor: accountData.authorizedPerson.trim() === '' ? 'error.main' : 'inherit' 
                    },
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.authorizedPersonPhone')}
                value={accountData.authorizedPersonPhone}
                onChange={(e) => handleChange('authorizedPersonPhone', e.target.value)}
                placeholder={t('current.modals.addAccount.placeholders.phone')}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* İletişim Bilgileri Bölümü */}
      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('current.modals.addAccount.sections.contactInfo')}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.phone')}
                value={accountData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder={t('current.modals.addAccount.placeholders.phone')}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.mobilePhone')}
                value={accountData.mobilePhone}
                onChange={(e) => handleChange('mobilePhone', e.target.value)}
                placeholder={t('current.modals.addAccount.placeholders.phone')}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.email')}
                type="email"
                value={accountData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.website')}
                value={accountData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder={t('current.modals.addAccount.placeholders.website')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.taxOffice')}
                value={accountData.taxOffice}
                onChange={(e) => handleChange('taxOffice', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                label={t('current.modals.addAccount.fields.taxNumber')}
                value={accountData.taxNumber}
                onChange={(e) => handleChange('taxNumber', e.target.value)}
                sx={{ 
                  '& .MuiInputLabel-root': { 
                    color: accountData.taxNumber.trim() === '' ? 'error.main' : 'inherit' 
                  },
                  '& .MuiOutlinedInput-root': { 
                    '& fieldset': { 
                      borderColor: accountData.taxNumber.trim() === '' ? 'error.main' : 'inherit' 
                    },
                    '&:hover fieldset': { 
                      borderColor: accountData.taxNumber.trim() === '' ? 'error.main' : 'inherit' 
                    },
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.tradeRegisterNumber')}
                value={accountData.tradeRegisterNumber}
                onChange={(e) => handleChange('tradeRegisterNumber', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Adres Bilgileri Bölümü */}
      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HomeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('current.modals.addAccount.sections.addressInfo')}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.country')}
                value={accountData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.city')}
                value={accountData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.district')}
                value={accountData.district}
                onChange={(e) => handleChange('district', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.address')}
                multiline
                rows={2}
                value={accountData.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.accountCode')}
                value={accountData.accountCode}
                onChange={(e) => handleChange('accountCode', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.postalCode')}
                value={accountData.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('current.modals.addAccount.fields.notes')}
                multiline
                rows={2}
                value={accountData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      
      {/* Şube Bilgisi ve Risk Limiti Bölümü */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {/* Şube Bilgisi Bölümü */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                  {t('current.modals.addAccount.sections.branchAssignment')}
                </Typography>
              </Box>
              
              <Autocomplete
                fullWidth
                size="small"
                options={branches}
                getOptionLabel={(option) => option.name}
                value={selectedBranch}
                disabled={!!lockedBranchId}
                onChange={(_, value) => handleBranchChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('current.modals.addAccount.fields.branch')}
                    placeholder={t('current.modals.addAccount.placeholders.branch')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
              />
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('current.modals.addAccount.branchHint')}
              </Typography>
            </Paper>
          </Grid>
          
          {/* Risk Limiti Bölümü */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                  {t('current.modals.addAccount.sections.riskLimit')}
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('current.modals.addAccount.fields.riskLimitAmount')}
                    type="number"
                    value={accountData.riskLimitAmount}
                    onChange={(e) => handleChange('riskLimitAmount', e.target.value)}
                    InputProps={{
                      startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('current.modals.addAccount.fields.currency')}
                    value={accountData.riskLimitCurrency}
                    onChange={(e) => handleChange('riskLimitCurrency', e.target.value)}
                  >
                    {availableCurrencies
                      .filter(currency => currency.active)
                      .map(currency => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.code} - {getCurrencySymbol(currency.code)}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('current.modals.addAccount.fields.paymentDueDays')}
                    type="number"
                    value={accountData.paymentDueDays}
                    onChange={(e) => handleChange('paymentDueDays', e.target.value)}
                    InputProps={{
                      startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('current.modals.addAccount.fields.riskLimitAction')}
                    value={accountData.riskLimitAction}
                    onChange={(e) => handleChange('riskLimitAction', e.target.value)}
                  >
                    <MenuItem value="işlem yaptır">{t('current.modals.addAccount.riskActions.allow')}</MenuItem>
                    <MenuItem value="işlem yaptırma">{t('current.modals.addAccount.riskActions.block')}</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={accountData.preventNegativeBalance}
                        onChange={(e) => handleChange('preventNegativeBalance', e.target.checked)}
                        name="preventNegativeBalance"
                        color="primary"
                      />
                    }
                    label={t('current.modals.addAccount.fields.preventNegativeBalance')}
                  />
                </Grid>
              </Grid>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('current.modals.addAccount.riskHint')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* E-Ticaret / B2B Bölümü (Faz 5.2) */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorefrontOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('current.modals.addAccount.b2b.sectionTitle', { defaultValue: 'E-Ticaret / B2B' })}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accountData.isEcommerceUser}
                    onChange={(e) => handleChange('isEcommerceUser', e.target.checked)}
                    color="primary"
                  />
                }
                label={t('current.modals.addAccount.b2b.isEcommerceUser', { defaultValue: 'E-ticaret kullanıcısı' })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accountData.isB2BDealer}
                    onChange={(e) => handleChange('isB2BDealer', e.target.checked)}
                    color="primary"
                  />
                }
                label={t('current.modals.addAccount.b2b.isB2BDealer', { defaultValue: 'Bayi (B2B)' })}
              />
            </Grid>

            {accountData.isEcommerceUser && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label={t('current.modals.addAccount.b2b.username', { defaultValue: 'Kullanıcı Adı' })}
                    value={accountData.ecomUsername}
                    onChange={(e) => handleChange('ecomUsername', e.target.value)}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    required={!linkedMember}
                    size="small"
                    type="password"
                    label={linkedMember
                      ? t('current.modals.addAccount.b2b.passwordNew', { defaultValue: 'Yeni Şifre (boşsa değişmez)' })
                      : t('current.modals.addAccount.b2b.password', { defaultValue: 'Şifre' })}
                    value={accountData.ecomPassword}
                    onChange={(e) => handleChange('ecomPassword', e.target.value)}
                    autoComplete="new-password"
                  />
                </Grid>
              </>
            )}

            {accountData.isB2BDealer && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t('current.modals.addAccount.b2b.discountPercent', { defaultValue: 'E-Ticaret İskontosu (%)' })}
                    value={accountData.ecommerceDiscountPercent}
                    onChange={(e) => handleChange('ecommerceDiscountPercent', e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('current.modals.addAccount.b2b.priceBasis', { defaultValue: 'İskonto Baz Fiyatı' })}
                    value={accountData.ecommercePriceBasis}
                    onChange={(e) => handleChange('ecommercePriceBasis', e.target.value)}
                  >
                    <MenuItem value="SalePrice">{t('current.modals.addAccount.b2b.priceBasisSale', { defaultValue: 'Satış Fiyatı' })}</MenuItem>
                    <MenuItem value="ListPrice">{t('current.modals.addAccount.b2b.priceBasisList', { defaultValue: 'Liste Fiyatı' })}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={accountData.blockOrdersWhenOverdue}
                        onChange={(e) => handleChange('blockOrdersWhenOverdue', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('current.modals.addAccount.b2b.blockOrdersWhenOverdue', { defaultValue: 'Vadesi geçmiş borç varken vadeli sipariş verilemesin' })}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {linkedMember
              ? t('current.modals.addAccount.b2b.linkedHint', {
                  defaultValue: 'Bu cariye bağlı bir e-ticaret üyesi var ({{email}}). Kullanıcı adı/şifre değişiklikleri bu üyeye uygulanır.',
                  email: linkedMember.email
                })
              : t('current.modals.addAccount.b2b.hint', { defaultValue: '"E-ticaret kullanıcısı" işaretlenirse, kaydetme sırasında bu cariye bağlı bir mağaza girişi (kullanıcı adı/şifre) oluşturulur. "Bayi (B2B)" işaretli carilere e-ticarette iskonto ve cari ile ödeme uygulanır.' })}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
  
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0
        }}>
          <Typography variant="h6">
            {isEditMode ? t('current.modals.addAccount.titleEdit') : t('current.modals.addAccount.titleAdd')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setInfoDialogOpen(true)}
              aria-label={t('current.menu.information')}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label={t('current.actions.close')}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderFormContent()
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose}
            disabled={loading}
          >
            {t('current.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={loading}
          >
            {loading 
              ? (isEditMode ? t('current.modals.addAccount.updating') : t('current.modals.addAccount.saving')) 
              : (isEditMode ? t('current.modals.addAccount.update') : t('current.modals.addAccount.save'))
            }
          </Button>
        </DialogActions>

        {/* Cari Grup Modal */}
        <AccountGroupModal 
          open={isGroupModalOpen}
          onClose={handleCloseGroupModal}
        />
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('current.modals.addAccount.infoDialog.title', { defaultValue: 'Cari Hesap Bilgilendirme' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.addAccount.infoDialog.description', {
              defaultValue:
                'Bu sayfa, cari hesabın sistemde neyi temsil ettiğini ve alanların hangi işlevlere hizmet ettiğini açıklar.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.addAccount.infoDialog.currentIdentityTitle', { defaultValue: 'Cari Kimlik ve Sınıflama' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.addAccount.infoDialog.currentIdentityText', {
              defaultValue:
                'Hesap/Firma adı, cari grup ve cari kodu alanları, cari hesabın ticari kimliğini belirler. Bu bilgiler listeleme, filtreleme ve raporlama tarafında kaydın doğru sınıflandırılmasını sağlar.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.addAccount.infoDialog.communicationTitle', { defaultValue: 'İletişim ve Vergisel Kayıtlar' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.addAccount.infoDialog.communicationText', {
              defaultValue:
                'Telefon, e-posta, vergi dairesi ve vergi numarası gibi alanlar; fatura, tahsilat ve yasal belge süreçlerinde kullanılan temel referans kayıtlarıdır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.addAccount.infoDialog.addressTitle', { defaultValue: 'Adres ve Operasyonel Süreçler' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.addAccount.infoDialog.addressText', {
              defaultValue:
                'Ülke, il, ilçe ve açık adres bilgileri; sevkiyat, ziyaret, teslimat ve resmi yazışma süreçlerinde operasyonel doğruluk sağlar.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.addAccount.infoDialog.riskTitle', { defaultValue: 'Risk ve Finans Kontrolü' })}
          </Typography>
          <Typography variant="body2">
            {t('current.modals.addAccount.infoDialog.riskText', {
              defaultValue:
                'Risk limiti, vade ve negatif bakiyeyi engelleme alanları; cariye yapılacak işlemlerde finansal kontrol kurallarını tanımlar ve risk yönetimini destekler.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            {t('current.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAccount; 
